import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { authManager, InvitationService } from '@df/api';
import { useNavigate } from 'react-router-dom';
import {
  clearScopedInvitationContinuation,
  clearScopedInvitationState,
  clearScopedInvitationToken,
  getScopedInvitationContinuation,
  getScopedInvitationToken,
  setScopedInvitationContinuation,
} from '../../utils/scopedInvitationSession';
import { getSafeApiErrorMessage, isNetworkError } from '../../utils/errorUtils';
import ContinuationShell from '../../components/ContinuationShell';

const TERMINAL_REASON_COPY = {
  accepted: {
    title: 'Invitation already accepted',
    message: 'This invitation has already been used and cannot be continued here.',
  },
  revoked: {
    title: 'Invitation revoked',
    message: 'This invitation is no longer active.',
  },
  expired: {
    title: 'Invitation expired',
    message: 'This invitation has expired and can no longer be used.',
  },
  invalid: {
    title: 'Invitation invalid',
    message: 'This invitation link is not valid.',
  },
  not_found: {
    title: 'Invitation not found',
    message: 'This invitation link could not be recognized.',
  },
  inactive_team: {
    title: 'Team unavailable',
    message: 'The team linked to this invitation is currently unavailable.',
  },
  inactive_organization: {
    title: 'Organization unavailable',
    message: 'The organization linked to this invitation is currently unavailable.',
  },
  claimed_by_other_user: {
    title: 'Invitation unavailable',
    message: 'This invitation is already claimed by another account.',
  },
  inactive_user: {
    title: 'Account unavailable',
    message: 'This invitation cannot be accepted with the current account state.',
  },
};

const formatReasonLabel = (value) =>
  String(value || '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const formatTimestamp = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const isPendingInvitationResponse = (response) =>
  response &&
  response.valid === true &&
  response.reason === 'ok' &&
  response.status === 'pending' &&
  typeof response.kind === 'string' &&
  typeof response.expiresAt === 'string' &&
  typeof response.emailHint === 'string' &&
  typeof response.emailRequired === 'boolean' &&
  typeof response.requiresAuthentication === 'boolean' &&
  typeof response.requiresVerifiedEmail === 'boolean' &&
  response.presentation &&
  typeof response.presentation === 'object';

const buildPendingFacts = (response) => {
  const presentation = response.presentation || {};
  const facts = [
    presentation.teamName || presentation.approvedTeamName
      ? {
          label: 'Team',
          value: presentation.teamName || presentation.approvedTeamName,
        }
      : null,
    presentation.role
      ? {
          label: 'Role',
          value: formatReasonLabel(presentation.role),
        }
      : null,
    response.emailHint
      ? {
          label: 'Email',
          value: response.emailHint,
        }
      : null,
    {
      label: 'Requires sign in',
      value: response.requiresAuthentication ? 'Yes' : 'No',
    },
    {
      label: 'Requires verified email',
      value: response.requiresVerifiedEmail ? 'Yes' : 'No',
    },
    {
      label: 'Expires',
      value: formatTimestamp(response.expiresAt),
    },
  ];

  return facts.filter(Boolean);
};

const buildValidationFailureMessage = (error) => {
  if (isNetworkError(error)) {
    return 'Network error. Please check your connection and try again.';
  }

  return getSafeApiErrorMessage(
    error,
    'We could not validate this invitation right now. Please try again.',
  );
};

const buildRedeemFailureMessage = (error) => {
  if (isNetworkError(error)) {
    return 'Network error. Please check your connection and try again.';
  }

  return 'We could not accept this invitation right now. Please try again.';
};

const getRedeemConflictReason = (error) => {
  if (Number(error?.status) !== 409) {
    return null;
  }

  if (error?.data?.code !== 'SCOPED_INVITATION_REDEMPTION_CONFLICT') {
    return null;
  }

  const reason = error?.data?.fields?.reason;
  return typeof reason === 'string' ? reason : null;
};

const isInvalidRedeemTokenError = (error) =>
  Number(error?.status) === 400 && error?.data?.code === 'INVALID_SCOPED_INVITATION';

const ScopedInvitation = () => {
  const navigate = useNavigate();
  const [viewState, setViewState] = useState({ kind: 'validating' });
  const [invitation, setInvitation] = useState(null);

  const continuation = getScopedInvitationContinuation();
  const invitationFacts = useMemo(
    () => (invitation ? buildPendingFacts(invitation) : []),
    [invitation],
  );

  const setTerminalState = useCallback((reason) => {
    clearScopedInvitationState();
    setInvitation(null);
    setViewState({ kind: 'terminal', reason });
  }, []);

  const validateInvitation = useCallback(async () => {
    const token = getScopedInvitationToken();
    if (!token) {
      setInvitation(null);
      setViewState({ kind: 'missing' });
      return;
    }

    setViewState({ kind: 'validating' });

    try {
      const response = await InvitationService.validateScopedInvitationToken(token);

      if (isPendingInvitationResponse(response)) {
        setInvitation(response);
        setViewState({
          kind: authManager.isAuthenticated() ? 'valid_authenticated_ready' : 'valid_needs_auth',
        });
        return;
      }

      if (response?.valid === false && TERMINAL_REASON_COPY[response.reason]) {
        setTerminalState(response.reason);
        return;
      }

      setViewState({
        kind: 'recoverable_error',
        phase: 'validate',
        message: 'We could not validate this invitation right now. Please try again.',
      });
    } catch (error) {
      if (Number(error?.status) === 400) {
        setViewState({
          kind: 'recoverable_error',
          phase: 'validate',
          message: 'We could not validate this invitation right now. Please try again.',
        });
        return;
      }

      setViewState({
        kind: 'recoverable_error',
        phase: 'validate',
        message: buildValidationFailureMessage(error),
      });
    }
  }, [setTerminalState]);

  const redeemInvitation = useCallback(async () => {
    const token = getScopedInvitationToken();
    if (!token) {
      setInvitation(null);
      setViewState({ kind: 'missing' });
      return;
    }

    setViewState({ kind: 'redeeming' });

    try {
      const response = await InvitationService.redeemScopedInvitationToken(token);

      if (response?.redeemed === true) {
        clearScopedInvitationState();
        setViewState({
          kind: 'redeemed',
          alreadyRedeemed: response.alreadyRedeemed === true,
        });
        return;
      }

      setViewState({
        kind: 'recoverable_error',
        phase: 'redeem',
        message: 'We could not accept this invitation right now. Please try again.',
      });
    } catch (error) {
      if (Number(error?.status) === 401) {
        authManager.clearTokens();
        setScopedInvitationContinuation('login');
        navigate('/login', { replace: true });
        return;
      }

      const conflictReason = getRedeemConflictReason(error);
      if (conflictReason === 'email_mismatch') {
        setViewState({ kind: 'wrong_account' });
        return;
      }

      if (conflictReason === 'verification_required') {
        setScopedInvitationContinuation('verify');
        setViewState({ kind: 'verification_required' });
        return;
      }

      if (conflictReason && TERMINAL_REASON_COPY[conflictReason]) {
        setTerminalState(conflictReason);
        return;
      }

      if (isInvalidRedeemTokenError(error)) {
        setTerminalState('invalid');
        return;
      }

      setViewState({
        kind: 'recoverable_error',
        phase: 'redeem',
        message: buildRedeemFailureMessage(error),
      });
    }
  }, [navigate, setTerminalState]);

  useEffect(() => {
    validateInvitation();
  }, [validateInvitation]);

  const handleCancel = useCallback(() => {
    clearScopedInvitationState();
    navigate('/login', { replace: true });
  }, [navigate]);

  const handleSignIn = useCallback(() => {
    setScopedInvitationContinuation('login');
    navigate('/login');
  }, [navigate]);

  const handleCreateAccount = useCallback(() => {
    setScopedInvitationContinuation('signup');
    navigate('/signup');
  }, [navigate]);

  const handleWrongAccountSignOut = useCallback(() => {
    setScopedInvitationContinuation('login');
    navigate('/login?logout=1');
  }, [navigate]);

  const handleVerificationHelp = useCallback(() => {
    setScopedInvitationContinuation('verify');
    navigate('/resend-verification');
  }, [navigate]);

  const headerTitle = (() => {
    if (viewState.kind === 'valid_authenticated_ready' || viewState.kind === 'valid_needs_auth') {
      return invitation?.presentation?.title || 'Invitation validated';
    }
    if (viewState.kind === 'redeemed') {
      return 'Access activated';
    }
    if (viewState.kind === 'wrong_account') {
      return 'Wrong account signed in';
    }
    if (viewState.kind === 'verification_required') {
      return 'Email verification required';
    }
    if (viewState.kind === 'terminal') {
      return TERMINAL_REASON_COPY[viewState.reason].title;
    }
    if (viewState.kind === 'recoverable_error') {
      return viewState.phase === 'redeem'
        ? 'Invitation acceptance unavailable'
        : 'Invitation validation unavailable';
    }
    if (viewState.kind === 'missing') {
      return 'Invitation link missing';
    }
    return 'Validating invitation';
  })();

  return (
    <ContinuationShell>
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography variant="overline" sx={{ letterSpacing: '0.14em', color: '#5f6b84' }}>
                Scoped invitation
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#182033' }}>
                {headerTitle}
              </Typography>
            </Stack>

            {viewState.kind === 'validating' && (
              <Stack spacing={2} alignItems="center" textAlign="center">
                <CircularProgress size={40} />
                <Typography color="text.secondary">
                  Validating your invitation…
                </Typography>
              </Stack>
            )}

            {viewState.kind === 'redeeming' && (
              <Stack spacing={2} alignItems="center" textAlign="center">
                <CircularProgress size={40} />
                <Typography color="text.secondary">
                  Accepting your invitation…
                </Typography>
              </Stack>
            )}

            {viewState.kind === 'missing' && (
              <Alert severity="info">
                This invitation link does not include a usable token. Request a fresh invitation and try again.
              </Alert>
            )}

            {viewState.kind === 'terminal' && (
              <Alert severity="info">
                {TERMINAL_REASON_COPY[viewState.reason].message}
              </Alert>
            )}

            {viewState.kind === 'recoverable_error' && (
              <Alert severity="warning">
                {viewState.message}
              </Alert>
            )}

            {viewState.kind === 'redeemed' && (
              <Alert severity="success">
                Your DataFre8 access has been updated successfully.
              </Alert>
            )}

            {viewState.kind === 'wrong_account' && (
              <Alert severity="warning">
                You are signed in with a different account than this invitation expects. Sign out and continue with the invited account.
              </Alert>
            )}

            {viewState.kind === 'verification_required' && (
              <Alert severity="info">
                Verify your account email before accepting this invitation. After verification, return here and accept it explicitly.
              </Alert>
            )}

            {(
              invitation &&
              [
                'valid_needs_auth',
                'valid_authenticated_ready',
                'wrong_account',
                'verification_required',
                'recoverable_error',
              ].includes(viewState.kind)
            ) && (
              <Stack spacing={2.5}>
                {viewState.kind === 'valid_needs_auth' && continuation === 'verify' ? (
                  <Alert severity="success">
                    Your account is ready. Sign in to continue with this invitation, then accept it explicitly.
                  </Alert>
                ) : viewState.kind === 'valid_needs_auth' ? (
                  <Alert severity="success">
                    This invitation is valid and still pending. Sign in or create the invited account to continue.
                  </Alert>
                ) : viewState.kind === 'valid_authenticated_ready' ? (
                  <Alert severity="success">
                    This invitation is valid and ready. Accept it explicitly to complete the flow.
                  </Alert>
                ) : null}

                <Stack spacing={1.5}>
                  {invitationFacts.map((fact) => (
                    <Box
                      key={fact.label}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                        py: 1.25,
                        borderBottom: '1px solid #edf1f7',
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, color: '#36415c' }}>
                        {fact.label}
                      </Typography>
                      <Typography sx={{ color: '#182033', textAlign: 'right' }}>
                        {fact.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              {viewState.kind === 'valid_needs_auth' && (
                <>
                  <Button variant="contained" onClick={handleSignIn}>
                    Sign in
                  </Button>
                  {continuation !== 'verify' && (
                    <Button variant="outlined" onClick={handleCreateAccount}>
                      Create account
                    </Button>
                  )}
                </>
              )}

              {viewState.kind === 'valid_authenticated_ready' && (
                <Button variant="contained" onClick={redeemInvitation}>
                  Accept / Continue
                </Button>
              )}

              {viewState.kind === 'wrong_account' && (
                <Button variant="contained" onClick={handleWrongAccountSignOut}>
                  Sign out and continue
                </Button>
              )}

              {viewState.kind === 'verification_required' && (
                <Button variant="contained" onClick={handleVerificationHelp}>
                  Request verification email
                </Button>
              )}

              {viewState.kind === 'recoverable_error' && (
                <Button
                  variant="contained"
                  onClick={viewState.phase === 'redeem' ? redeemInvitation : validateInvitation}
                >
                  {viewState.phase === 'redeem' ? 'Retry acceptance' : 'Retry validation'}
                </Button>
              )}

              {viewState.kind === 'redeemed' && (
                <Button variant="contained" onClick={() => navigate('/portal?handoff=lantern-v3')}>
                  Continue to DataFre8
                </Button>
              )}

              {viewState.kind !== 'validating' && viewState.kind !== 'redeeming' && (
                <Button
                  variant={
                    ['recoverable_error', 'valid_needs_auth', 'valid_authenticated_ready', 'wrong_account', 'verification_required']
                      .includes(viewState.kind)
                      ? 'outlined'
                      : 'contained'
                  }
                  color={viewState.kind === 'valid_authenticated_ready' ? 'inherit' : 'primary'}
                  onClick={handleCancel}
                >
                  {viewState.kind === 'valid_authenticated_ready'
                    ? 'Cancel and return to sign in'
                    : 'Return to sign in'}
                </Button>
              )}
            </Stack>
          </Stack>
    </ContinuationShell>
  );
};

export default ScopedInvitation;
